import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Diamond, Coins, Sparkle } from "@phosphor-icons/react";
import { playDiamondChing } from "@/lib/confetti";

// The biggest celebration in the app: 5 rubies fusing into a diamond.
// Full-screen takeover above every coin set and level-up moment.
export default function DiamondCelebration({ forged = 1, total = 1, soundOn = true, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    // Crystal ching lands exactly when the diamond pops out of the coin implosion.
    const soundsAllowed = soundOn && localStorage.getItem("coinSoundsOff") !== "1";
    const timer = soundsAllowed ? setTimeout(playDiamondChing, 700) : null;
    return () => {
      document.body.style.overflow = "";
      if (timer) clearTimeout(timer);
    };
  }, [soundOn]);

  const sparks = Array.from({ length: 14 });

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[95] flex items-center justify-center p-5 cursor-pointer"
      style={{ background: "radial-gradient(circle at 50% 42%, #0e2a3a 0%, #06111a 70%)" }}
      onClick={onClose}
      data-testid="diamond-celebration"
    >
      <div className="text-center select-none">
        <div className="relative inline-block">
          {sparks.map((_, i) => (
            <motion.span
              key={i}
              className="absolute left-1/2 top-1/2"
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
              animate={{
                x: Math.cos((i / sparks.length) * Math.PI * 2) * 150,
                y: Math.sin((i / sparks.length) * Math.PI * 2) * 150,
                opacity: [0, 1, 0], scale: [0.4, 1.15, 0.5],
              }}
              transition={{ duration: 1.8, delay: 0.55 + (i % 5) * 0.12, repeat: Infinity, repeatDelay: 1.4 }}
            >
              <Sparkle size={22} weight="fill" color="#7DD3FC" />
            </motion.span>
          ))}
          <motion.span
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            initial={{ scale: 1, opacity: 1 }}
            animate={{ scale: 0.1, opacity: 0, rotate: 260 }}
            transition={{ duration: 0.8, ease: "easeIn" }}
          >
            <Coins size={96} weight="fill" color="#FFC400" />
          </motion.span>
          <motion.span
            className="inline-block"
            initial={{ scale: 0, rotate: -25 }}
            animate={{ scale: [0, 1.35, 1], rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: "backOut" }}
          >
            <motion.span
              className="inline-block"
              animate={{ filter: ["drop-shadow(0 0 18px #7DD3FC)", "drop-shadow(0 0 55px #7DD3FC)", "drop-shadow(0 0 18px #7DD3FC)"] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            >
              <Diamond size={170} weight="fill" color="#7DD3FC" />
            </motion.span>
          </motion.span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15 }}
          className="font-display font-black text-4xl sm:text-5xl mt-6 tracking-tight"
          style={{ color: "#fff", textShadow: "0 2px 24px rgba(125,211,252,0.55)" }}
        >
          DIAMOND FORGED!
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="font-display font-bold text-lg mt-2"
          style={{ color: "#7DD3FC" }}
          data-testid="diamond-celebration-detail"
        >
          5 RUBIES &rarr; {forged > 1 ? `${forged} DIAMONDS` : "1 DIAMOND"}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
          className="text-white/60 text-sm mt-1"
        >
          You now hold {total} diamond{total === 1 ? "" : "s"}. Legendary.
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9 }}
          className="mt-7 px-8 py-3 rounded-full font-display font-bold text-sm uppercase tracking-wide border-0 cursor-pointer"
          style={{ background: "#7DD3FC", color: "#06111a" }}
          onClick={onClose}
          data-testid="diamond-celebration-continue"
        >
          Keep stacking
        </motion.button>
      </div>
    </motion.div>,
    document.body
  );
}
